// --- Framework
import React from 'react';
import PropTypes from 'prop-types';

// --- External tools
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation, Trans } from 'react-i18next';

// --- IO
import API from 'io/API';

// --- Logic
import TranslationKey from 'logic/enums/TranslationKey';
import AuthenticationStatus from 'logic/enums/AuthenticationStatus';

// --- External components
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import Dialog from '@material-ui/core/Dialog';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

// --- Components
import { Transition } from 'App';
import HouseCard from 'visual/components/cards/HouseCard';
import EndOfListCard from 'visual/components/cards/EndOfListCard';
import HouseInspector from 'visual/components/modals/HouseInspector';
import VirtualizedList from 'visual/components/_/list/VirtualizedList';

// --- Style
const styles = theme => ({});


class Bookmarks extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			bookmarkedHouseList: [],
			loadingBookmarkedHouses: false,
		};

		this.fetchBookmarkedHouses = this.fetchBookmarkedHouses.bind(this);
	}

	componentDidMount() {
		this.fetchBookmarkedHouses();
	}

	componentDidUpdate(prevProps) {
		const prevBookmarkKeys = prevProps.bookmarkDictionary != null ? Object.keys(prevProps.bookmarkDictionary).sort().join(',') : '';
		const nextBookmarkKeys = this.props.bookmarkDictionary != null ? Object.keys(this.props.bookmarkDictionary).sort().join(',') : '';
		if (prevBookmarkKeys !== nextBookmarkKeys || prevProps.authenticationStatus !== this.props.authenticationStatus)
			this.fetchBookmarkedHouses();
	}

	async fetchBookmarkedHouses() {
		const { authenticationStatus, bookmarkDictionary } = this.props;
		if (authenticationStatus !== AuthenticationStatus.Authenticated) {
			this.setState({ bookmarkedHouseList: [], loadingBookmarkedHouses: false });
			return;
		}

		const ids = bookmarkDictionary != null ? Object.keys(bookmarkDictionary).filter(Boolean) : [];
		if (ids.length <= 0) {
			this.setState({ bookmarkedHouseList: [], loadingBookmarkedHouses: false });
			return;
		}

		await this.setState({ loadingBookmarkedHouses: true });

		const responses = await Promise.allSettled(ids.map(id => API.getHouse(id)));
		const bookmarkedHouseList = responses.reduce((result, r, index) => {
			if (r.status !== 'fulfilled' || r.value == null || r.value.data == null)
				return result;
			const id = ids[index];
			result.push({
				id,
				...r.value.data,
			});
			return result;
		}, []);

		this.setState({ bookmarkedHouseList, loadingBookmarkedHouses: false });
	}

	render() {
		const {
			props: {
				t,
				i18n,
				classes,
				history,
				contactData,
				adCategories,
				toggleBookmark,
				bookmarkDictionary,
				authenticationStatus,
				supportedHouseStatus,
				supportedPropertyTypes,
				match: { params: { id } },
			},
			state: {
				bookmarkedHouseList,
				loadingBookmarkedHouses,
			},
		} = this;

		let notSignedIn = null;
		if (authenticationStatus !== AuthenticationStatus.Authenticated) {
			notSignedIn = (
				<Grid
					item
					xs={12}
					sm="auto"
					style={{ margin: 'auto' }}
				>
					<Alert severity="warning">
						<Trans i18nKey={TranslationKey.page_favorites_caveat_not_signed_in} />
					</Alert>
				</Grid>
			);
		}

		let virtualizedList = null;
		if (authenticationStatus === AuthenticationStatus.Authenticated && loadingBookmarkedHouses)
			virtualizedList = <CircularProgress size={56} style={{ margin: '23px auto' }}/>;
		else {
			virtualizedList = (
				<VirtualizedList
					preloadingOffset={2}
					itemCount={bookmarkedHouseList.length}
					windowHeight={window.innerHeight - 72 - 56 - 48 - 8}
					computeSizes={(rowWidth) => {
						const maxItemWidth = 416;

						const howManyItemsCanFit = Math.max(Math.ceil(rowWidth / (maxItemWidth * 1.1)), 1);

						let freeSpaceRatio = (rowWidth / maxItemWidth) / howManyItemsCanFit;

						if (freeSpaceRatio > 1)
							freeSpaceRatio = 1;

						const itemWidth = rowWidth < maxItemWidth ? rowWidth : maxItemWidth * freeSpaceRatio;

						const rowHeight = (itemWidth * 0.5625) + 86 + 12;

						return {
							itemWidth,
							rowHeight,
						};
					}}
					renderItem={(key, index, style) => (
						<div
							key={key}
							style={{
								padding: '6px',
								...style,
							}}
						>
							<HouseCard
								bookmarked={bookmarkDictionary[bookmarkedHouseList[index].id] != null}
								i18n={i18n}
								toggleBookmark={toggleBookmark}
								house={bookmarkedHouseList[index]}
								supportedHouseStatus={supportedHouseStatus}
								to={`/bookmarks/${bookmarkedHouseList[index].id}`}
							/>
						</div>
					)}
				/>
			);
		}

		return (
			<Container
				disableGutters
				className="page"
			>
				<Dialog
					fullScreen
					open={id != null}
					TransitionComponent={Transition}
					aria-labelledby="alert-dialog-slide-title"
					aria-describedby="alert-dialog-slide-description"
				>
					<HouseInspector
						t={t}
						id={id}
						i18n={i18n}
						houseList={bookmarkedHouseList}
						contactData={contactData}
						adCategories={adCategories}
						toggleBookmark={toggleBookmark}
						onClose={() => history.push('/bookmarks')}
						supportedHouseStatus={supportedHouseStatus}
						bookmarked={bookmarkDictionary[id] != null}
						supportedPropertyTypes={supportedPropertyTypes}
					/>
				</Dialog>
				<Grid
					container
					spacing={2}
				>
					<Grid
						item
						xs={12}
					>
						<Typography
							variant="h5"
							component="h1"
							style={{ textTransform: 'capitalize' }}
						>
							{t(TranslationKey.page_favorites_title)}
						</Typography>
					</Grid>
					{/*
					<Grid
						item
						container
						direction="row"
						spacing={1}
					>
						<Chip
							clickable
							color="primary"
							onClick={openHouseListFilterModal}
							style={{ textTransform: 'capitalize', fontWeight: 700 }}
							label={!isFilteredList ? t(TranslationKey.button_filter_list) : t(TranslationKey.button_filter_list_active)}
							variant={!isFilteredList ? 'outlined' : null}
							icon={!isFilteredList ? <Icon>filter_list</Icon> : null}
							onDelete={isFilteredList ? clearHouseListFilters : null}
							// deleteIcon={houseListFiltersEnabled ? <Icon>toggle_on</Icon> : <Icon>toggle_off</Icon>}
							avatar={isFilteredList ? <Avatar style={{ fontWeight: 400 }}>{houseListFilterCount}</Avatar> : null}
						/>
					</Grid>
					*/}
					{virtualizedList}
					<Grid
						item
						xs={12}
					>
						<EndOfListCard
							isFilteredList={false}
							itemsCount={bookmarkedHouseList.length}
							translationKey={TranslationKey.bookmarks_end_of_list}
							translationKeyWhenEmpty={TranslationKey.bookmarks_end_of_list_empty}
							// translationKeyWhenFiltered={TranslationKey.bookmarks_end_of_list_filtered}
							displayAsAlert={bookmarkedHouseList.length <= 0 ? 'info' : null}
							// displayAsAlert={bookmarkedHouseList.length <= 0 && !isFilteredList ? 'info' : null}
						/>
					</Grid>
					{notSignedIn}
				</Grid>
			</Container>
		);
	}
}

Bookmarks.propTypes = {
	t: PropTypes.func,
	i18n: PropTypes.object,
	match: PropTypes.object,
	history: PropTypes.object,
	contactData: PropTypes.object,
	adCategories: PropTypes.object,
	toggleBookmark: PropTypes.func,
	// houseListFilterCount: PropTypes.number,
	bookmarkDictionary: PropTypes.object,
	classes: PropTypes.object.isRequired,
	// clearHouseListFilters: PropTypes.func,
	// toggleHouseListFilters: PropTypes.func,
	// houseListFiltersEnabled: PropTypes.bool,
	// openHouseListFilterModal: PropTypes.func,
	supportedHouseStatus: PropTypes.object.isRequired,
	supportedPropertyTypes: PropTypes.object.isRequired,
	authenticationStatus: PropTypes.oneOf(Object.values(AuthenticationStatus)).isRequired,
};

Bookmarks.defaultProps = {
	// houseListFiltersEnabled: false,
	toggleBookmark: () => console.warn('toggleBookmark method not provided.'),
	// clearHouseListFilters: () => console.warn('clearHouseListFilters method not provided.'),
	// toggleHouseListFilters: () => console.warn('toggleHouseListFilters method not provided.'),
	// openHouseListFilterModal: () => console.warn('openHouseListFilterModal method not provided.'),
};

export default compose(
	withTranslation(),
	withStyles(styles),
)(Bookmarks);
